import { StateCreator } from 'zustand';
import type { AppState } from '../../store/app-state';
import type { MetaSlice } from '../../store/types';
import { logger } from '../../utils/logger';
import { getWorkspace, type ToolId, type WorkspaceId } from '../layout/workspace-config';
import { applyWorkspaceToolReorder } from '../layout/workspace-tool-order';

function clearProjectDataSlices(actions: AppState): void {
    actions.showStructureActions.clearStructure();
    actions.propsActions.clearProps();
    actions.personnelActions.clearPersonnel();
    actions.runSheetActions.clearRows();
    actions.schedulerActions.clearScheduler();
    actions.soundActions.clearSound();
    actions.costumesActions.clearCostumes();
    actions.noteActions.clearNotes();
    actions.feedActions.clearFeed();
    actions.productionActions.clearProduction();
    actions.lightingActions.clearLighting();
    actions.promptBookActions.clearPromptBook();
    actions.attendanceActions.clearAttendance();
    actions.activityLogActions.clearActivityLog();
    actions.filesActions.clearFiles();
    actions.adminActions.clearAdmin();
    actions.performanceActions.clearPerformance();
    actions.flyActions.clearFly();
    actions.projectionActions.clearProjections();
    actions.actionItemActions.clearActionItems();
    actions.transcriptionActions.clearTranscription();
}

export const createMetaSlice: StateCreator<
    AppState,
    [["zustand/immer", never]],
    [],
    MetaSlice
> = (set, get) => ({
    auth: {
        user: null,
        isAuthenticated: false,
        isAuthModalOpen: false,
        initialized: false,
    },
    cloud: {
        enabled: false,
        orgId: null,
        projectId: null,
        role: null,
        member: null,
        lastSync: null,
    },
    ui: {
        activeTab: 'show-structure',
        activeWorkspace: null as WorkspaceId | null,
        sidebarCollapsed: false,
        surfaceScrollPositions: {},
        surfaceSubtabs: {},
        workspaceToolOrderOverrides: {},
    },
    projectMeta: {
        title: 'Untitled Show',

        director: '',
        sm: '',
        asm: '',
        leftLogo: '',
        rightLogo: '',
    },
    metaActions: {
        setUser: (user) => set((state: AppState) => {
            state.auth.user = user;
            state.auth.isAuthenticated = !!user;
            state.auth.initialized = true;
        }),
        setAuthModalOpen: (isOpen) => set((state: AppState) => {
            state.auth.isAuthModalOpen = isOpen;
        }),
        setActiveTab: (tabId) => set((state: AppState) => {
            state.ui.activeTab = tabId;
        }),
        setActiveWorkspace: (workspaceId: WorkspaceId | null) => set((state: AppState) => {
            state.ui.activeWorkspace = workspaceId;
        }),
        setSurfaceScrollPosition: (surfaceId, scrollTop) => {
            if (!surfaceId || !Number.isFinite(scrollTop)) {
                return;
            }

            const nextScrollTop = Math.max(0, Math.round(scrollTop));
            if (get().ui.surfaceScrollPositions[surfaceId] === nextScrollTop) {
                return;
            }

            set((state: AppState) => {
                state.ui.surfaceScrollPositions[surfaceId] = nextScrollTop;
            });
        },
        setSurfaceSubtab: (surfaceId, tabId) => {
            if (!surfaceId || !tabId) {
                return;
            }

            if (get().ui.surfaceSubtabs[surfaceId] === tabId) {
                return;
            }

            set((state: AppState) => {
                state.ui.surfaceSubtabs[surfaceId] = tabId;
            });
        },
        toggleSidebarCollapsed: () => set((state: AppState) => {
            state.ui.sidebarCollapsed = !state.ui.sidebarCollapsed;
        }),
        reorderWorkspaceTool: (workspaceId: WorkspaceId, activeId: ToolId, overId: ToolId) => set((state: AppState) => {
            const workspace = getWorkspace(workspaceId);

            if (!workspace) {
                return;
            }

            state.ui.workspaceToolOrderOverrides = applyWorkspaceToolReorder(
                workspace,
                state.ui.workspaceToolOrderOverrides,
                activeId,
                overId,
            );
        }),
        resetWorkspaceToolOrder: (workspaceId: WorkspaceId) => set((state: AppState) => {
            delete state.ui.workspaceToolOrderOverrides[workspaceId];
        }),
        updateProjectMeta: (updates) => set((state: AppState) => {
            Object.assign(state.projectMeta, updates);
        }),
        setCloudState: (updates: Partial<MetaSlice['cloud']>) => set((state: AppState) => {
            Object.assign(state.cloud, updates);
            if (updates.enabled === false) {
                state.cloud.member = null;
                state.cloud.hasLoadedCloudProject = false;
                state.cloud.hasLoadedProjectAccess = false;
                state.cloud.hasLoadedPersonnel = false;
                state.cloud.authoritativeResourceIds = undefined;
            }
        }),
        setProject: (project) => {
            const projectId = typeof project === 'string' ? project : project.projectId;
            const nextOrgId = typeof project === 'string' ? undefined : project.orgId;
            const currentOrgId = get().cloud.orgId;
            const currentProjectId = get().cloud.currentProjectId;
            const isSwitchingProject = Boolean(currentProjectId && currentProjectId !== projectId);
            const isSwitchingOrgScope = Boolean(currentProjectId
                && nextOrgId !== undefined
                && currentOrgId !== nextOrgId);
            const isSameProjectScope = currentProjectId === projectId
                && (nextOrgId === undefined || currentOrgId === nextOrgId);
            const shouldResetProjectAccess = !isSameProjectScope;
            
            // If switching projects, clear all project-specific data
            if (isSwitchingProject || isSwitchingOrgScope) {
                const previousScope = currentOrgId ? `${currentOrgId}/${currentProjectId}` : currentProjectId;
                const nextScope = nextOrgId ? `${nextOrgId}/${projectId}` : projectId;
                logger.store(`[Project Guard] Switching from ${previousScope} to ${nextScope}. Clearing all project data.`);
                
                // Clear all project-specific slices directly (outside of an Immer draft)
                clearProjectDataSlices(get());
            }
            
            // Set the new project context in its own update
            set((state: AppState) => {
                if (nextOrgId !== undefined) {
                    state.cloud.orgId = nextOrgId;
                }
                state.cloud.projectId = projectId;
                state.cloud.currentProjectId = projectId;
                if (shouldResetProjectAccess) {
                    state.cloud.hasLoadedProjectAccess = false;
                }
                state.cloud.hasLoadedPersonnel = false;
                if (isSwitchingProject || isSwitchingOrgScope) {
                    state.cloud.isBeta = undefined;
                    state.cloud.scriptTextAccess = undefined;
                    state.cloud.reportDistribution = undefined;
                    state.cloud.communicationAudiences = undefined;
                    state.cloud.hasLoadedCloudProject = false;
                    state.cloud.authoritativeResourceIds = undefined;
                }
            });
        },
        clearCloudSession: () => {
            logger.store('[Auth] Clearing cloud session on logout. Wiping all project data.');

            // 1. Clear ALL project-specific data slices directly first
            clearProjectDataSlices(get());

            // 2. Clear cloud metadata and UI state via Immer draft
            set((state: AppState) => {
                state.cloud.enabled = false;
                state.cloud.orgId = null;
                state.cloud.projectId = null;
                state.cloud.currentProjectId = undefined;
                state.cloud.role = null;
                state.cloud.member = null;
                state.cloud.lastSync = null;
                state.cloud.isBeta = undefined;
                state.cloud.scriptTextAccess = undefined;
                state.cloud.reportDistribution = undefined;
                state.cloud.communicationAudiences = undefined;
                state.cloud.hasLoadedCloudProject = false;
                state.cloud.hasLoadedProjectAccess = false;
                state.cloud.hasLoadedPersonnel = false;
                state.cloud.authoritativeResourceIds = undefined;

                // 3. Reset project meta to defaults
                state.projectMeta.title = 'Untitled Show';
                state.projectMeta.director = '';
                state.projectMeta.sm = '';
                state.projectMeta.asm = '';
                state.projectMeta.leftLogo = '';
                state.projectMeta.rightLogo = '';

                // 4. Reset UI state
                state.ui.activeTab = 'show-structure';
                state.ui.activeWorkspace = null;
                state.ui.surfaceScrollPositions = {};
                state.ui.surfaceSubtabs = {};
            });
        }
    },
});
